 import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
 import { supabase } from "@/integrations/supabase/client";
 import { toast } from "sonner";
 import { Loader2, Check, Crown, Building, Rocket } from "lucide-react";
 import { format } from "date-fns";
 
 // Stripe price IDs for subscription plans
 const SUBSCRIPTION_PLANS = {
   basic: {
     priceId: "price_1SxLvWI70UY7Bgnz6ZmgNQEg",
     productId: "prod_TvCKEB0IbwrqM0",
     name: "Basic",
     price: 29,
     description: "Perfect for single nursing home owners",
     features: [
       "1 nursing home listing",
       "20 media uploads",
       "View lead count",
       "Basic analytics",
     ],
     icon: Building,
   },
   professional: {
     priceId: "price_1SxLvkI70UY7BgnzxFBtrMTD",
     productId: "prod_TvCKfE9fxUZrKQ",
     name: "Professional",
     price: 79,
     description: "For growing care groups",
     features: [
       "3 nursing home listings",
       "100 media uploads",
       "Full lead management",
       "Priority support",
       "Verified badge",
     ],
     icon: Crown,
     popular: true,
   },
   enterprise: {
     priceId: "price_1SxLwLI70UY7Bgnzibavm1pB",
     productId: "prod_TvCL2UuucIcSdW",
     name: "Enterprise",
     price: 199,
     description: "For large care organizations",
     features: [
       "10 nursing home listings",
       "500 media uploads",
       "API access",
       "Dedicated support",
       "Priority listing placement",
       "Custom integrations",
     ],
     icon: Rocket,
   },
 };
 
 type PlanKey = keyof typeof SUBSCRIPTION_PLANS;
 
 interface SubscriptionStatus {
   subscribed: boolean;
   plan_name: string | null;
   plan_tier: string | null;
   subscription_end: string | null;
   price_id: string | null;
 }

export default function SubscriptionPage() {
   const [loading, setLoading] = useState(true);
   const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
   const [portalLoading, setPortalLoading] = useState(false);
   const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
 
   useEffect(() => {
     checkSubscription();
   }, []);
 
   const checkSubscription = async () => {
     try {
       const { data, error } = await supabase.functions.invoke("check-subscription");
       if (error) throw error;
       setSubscription(data);
     } catch (error) {
       console.error("Error checking subscription:", error);
       toast.error("Failed to load subscription status");
     } finally {
       setLoading(false);
     }
   };
 
   const handleSubscribe = async (planKey: PlanKey) => {
     setCheckoutLoading(planKey);
     try {
       const plan = SUBSCRIPTION_PLANS[planKey];
       const { data, error } = await supabase.functions.invoke("create-checkout", {
         body: { priceId: plan.priceId },
       });
       
       if (error) throw error;
       if (data?.url) {
         window.open(data.url, "_blank");
       }
     } catch (error) {
       console.error("Error creating checkout:", error);
       toast.error("Failed to start checkout. Please try again.");
     } finally {
       setCheckoutLoading(null);
     }
   };
 
   const handleManageSubscription = async () => {
     setPortalLoading(true);
     try {
       const { data, error } = await supabase.functions.invoke("customer-portal");
       if (error) throw error;
       if (data?.url) {
         window.open(data.url, "_blank");
       }
     } catch (error) {
       console.error("Error opening portal:", error);
       toast.error("Failed to open billing portal. Please try again.");
     } finally {
       setPortalLoading(false);
     }
   };
 
   const getCurrentPlanTier = (): PlanKey | null => {
     if (!subscription?.plan_tier) return null;
     return subscription.plan_tier as PlanKey;
   };
 
   const currentPlanTier = getCurrentPlanTier();
 
  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight">Subscription Management</h1>
            <p className="text-muted-foreground">
              Manage your nursing home subscription plan and billing
            </p>
          </div>
             
           {loading ? (
             <div className="flex items-center justify-center py-12">
               <Loader2 className="h-8 w-8 animate-spin text-primary" />
             </div>
           ) : subscription?.subscribed ? (
             <Card className="mb-8">
              <CardHeader>
                 <CardTitle className="flex items-center gap-2">
                   <Crown className="h-5 w-5 text-primary" />
                   Current Plan: {subscription.plan_name}
                 </CardTitle>
                <CardDescription>Your active subscription details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Plan</span>
                     <Badge variant="secondary">{subscription.plan_name}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Status</span>
                    <Badge variant="default">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                     <span className="font-medium">Next Billing Date</span>
                     <span>
                       {subscription.subscription_end 
                         ? format(new Date(subscription.subscription_end), "MMM d, yyyy")
                         : "N/A"}
                     </span>
                  </div>
                   <Button 
                     className="w-full mt-4" 
                     onClick={handleManageSubscription}
                     disabled={portalLoading}
                   >
                     {portalLoading ? (
                       <>
                         <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                         Loading...
                       </>
                     ) : (
                       "Manage Subscription"
                     )}
                   </Button>
                 </div>
              </CardContent>
            </Card>
           ) : null}

           <div className="grid gap-6 md:grid-cols-3">
             {(Object.entries(SUBSCRIPTION_PLANS) as [PlanKey, typeof SUBSCRIPTION_PLANS[PlanKey]][]).map(
               ([key, plan]) => {
                 const Icon = plan.icon;
                 const isCurrentPlan = currentPlanTier === key;
                 const isPopular = "popular" in plan && plan.popular;
 
                 return (
                   <Card 
                     key={key} 
                     className={`relative ${isPopular ? "border-primary shadow-lg" : ""} ${
                       isCurrentPlan ? "ring-2 ring-primary" : ""
                     }`}
                   >
                     {isPopular && (
                       <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                         <Badge className="bg-primary">Most Popular</Badge>
                       </div>
                     )}
                     {isCurrentPlan && (
                       <div className="absolute -top-3 right-4">
                         <Badge variant="secondary">Your Plan</Badge>
                       </div>
                     )}
                     <CardHeader className="text-center pt-8">
                       <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                         <Icon className="h-6 w-6 text-primary" />
                       </div>
                       <CardTitle>{plan.name}</CardTitle>
                       <CardDescription>{plan.description}</CardDescription>
                       <div className="mt-4">
                         <span className="text-4xl font-bold">€{plan.price}</span>
                         <span className="text-muted-foreground">/month</span>
                       </div>
                     </CardHeader>
                     <CardContent>
                       <ul className="space-y-3 mb-6">
                         {plan.features.map((feature, idx) => (
                           <li key={idx} className="flex items-center gap-2">
                             <Check className="h-4 w-4 text-primary flex-shrink-0" />
                             <span className="text-sm">{feature}</span>
                           </li>
                         ))}
                       </ul>
                       <Button
                         className="w-full"
                         variant={isCurrentPlan ? "outline" : isPopular ? "default" : "outline"}
                         disabled={isCurrentPlan || checkoutLoading !== null}
                         onClick={() => handleSubscribe(key)}
                       >
                         {checkoutLoading === key ? (
                           <>
                             <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                             Loading...
                           </>
                         ) : isCurrentPlan ? (
                           "Current Plan"
                         ) : subscription?.subscribed ? (
                           "Switch Plan"
                         ) : (
                           "Subscribe"
                         )}
                       </Button>
                     </CardContent>
                   </Card>
                 );
               }
             )}
          </div>
        </div>
      </div>
    </Layout>
  );
}